import { useState, useEffect, useRef } from 'react'
import {
  XMarkIcon,
  CalendarIcon,
  BellIcon,
  ClockIcon,
  EnvelopeIcon,
  UserGroupIcon,
  FlagIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import type { FollowUp, FollowUpType, Application } from '../types'

type Props = {
  isOpen: boolean
  application: Application | null // Pre-selected application (when opening from app detail)
  applications: Application[] // Available applications for selection
  editingFollowUp: FollowUp | null
  onConfirm: (data: {
    applicationId: string
    dueAt: string
    title: string
    type: FollowUpType
    note?: string
    followUpId?: string // For editing
  }) => void
  onCancel: () => void
}

const QUICK_OPTIONS = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'In 1 week', days: 7 },
  { label: 'In 2 weeks', days: 14 },
]

const TYPE_OPTIONS: { value: FollowUpType; label: string; icon: typeof ClockIcon }[] = [
  { value: 'reminder', label: 'Reminder', icon: ClockIcon },
  { value: 'email', label: 'Send email', icon: EnvelopeIcon },
  { value: 'interview', label: 'Interview', icon: UserGroupIcon },
  { value: 'deadline', label: 'Deadline', icon: FlagIcon },
]

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatTimeForInput = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

const addDays = (days: number): string => {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return formatDateForInput(date)
}

export const SetFollowUpModal = ({
  isOpen,
  application,
  applications,
  editingFollowUp,
  onConfirm,
  onCancel,
}: Props) => {
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('09:00')
  const [title, setTitle] = useState('')
  const [type, setType] = useState<FollowUpType>('reminder')
  const [note, setNote] = useState('')
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>('')
  const modalRef = useRef<HTMLDivElement>(null)

  const isEditing = !!editingFollowUp

  // Get the effective application (pre-selected or user-selected)
  const effectiveApplication = application ?? applications.find(a => a.id === selectedApplicationId) ?? null

  useEffect(() => {
    if (isOpen) {
      if (editingFollowUp) {
        const date = new Date(editingFollowUp.dueAt)
        setSelectedDate(formatDateForInput(date))
        setSelectedTime(formatTimeForInput(date))
        setTitle(editingFollowUp.title)
        setType(editingFollowUp.type)
        setNote(editingFollowUp.note ?? '')
        setSelectedApplicationId(editingFollowUp.applicationId)
      } else {
        setSelectedDate(addDays(1))
        setSelectedTime('09:00')
        setTitle('')
        setType('reminder')
        setNote('')
        setSelectedApplicationId(application?.id ?? '')
      }
    }
  }, [isOpen, editingFollowUp, application])

  // Auto-generate title based on type
  useEffect(() => {
    if (!isEditing && type && effectiveApplication) {
      if (type === 'reminder') {
        setTitle(`Reminder - ${effectiveApplication.company}`)
      } else if (type === 'email') {
        setTitle(`Follow up with ${effectiveApplication.company}`)
      } else if (type === 'interview') {
        setTitle(`Interview at ${effectiveApplication.company}`)
      } else if (type === 'deadline') {
        setTitle(`Application deadline - ${effectiveApplication.company}`)
      }
    }
  }, [type, effectiveApplication, isEditing])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onCancel()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onCancel])

  if (!isOpen) return null

  const handleQuickOption = (days: number) => {
    setSelectedDate(addDays(days))
  }

  const handleSubmit = () => {
    if (!selectedDate || !title.trim() || !effectiveApplication) return
    const dueAt = new Date(`${selectedDate}T${selectedTime}:00`).toISOString()
    onConfirm({
      applicationId: effectiveApplication.id,
      dueAt,
      title: title.trim(),
      type,
      note: note.trim() || undefined,
      followUpId: editingFollowUp?.id,
    })
  }

  const showApplicationSelector = !application && applications.length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="w-full max-w-md rounded-xl border border-app-line bg-app-surface shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-app-line px-4 py-3">
          <div className="flex items-center gap-2">
            <BellIcon className="h-5 w-5 text-sky-700 dark:text-app-accent" />
            <h2 className="text-base font-semibold text-app-ink font-display">
              {isEditing ? 'Edit Reminder' : 'Add Reminder'}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="rounded-lg p-1.5 text-app-muted hover:bg-app-surface hover:text-app-muted transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Application selector or info */}
          {showApplicationSelector ? (
            <div>
              <label className="block text-xs font-medium text-app-muted mb-1.5">Application</label>
              <div className="relative">
                <select
                  value={selectedApplicationId}
                  onChange={(e) => {
                    setSelectedApplicationId(e.target.value)
                    setTitle('') // Reset auto-generated title
                  }}
                  className="w-full appearance-none rounded-lg border border-app-line bg-app-surface px-3 py-2.5 pr-10 text-sm text-app-ink focus:border-app-accent focus:outline-none focus:ring-1 focus:ring-app-accent"
                >
                  <option value="">Select an application...</option>
                  {applications.filter(app => app.status !== 'archived').map((app) => (
                    <option key={app.id} value={app.id}>
                      {app.company}{app.positionTitle ? ` · ${app.positionTitle}` : ''}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-app-muted" />
              </div>
            </div>
          ) : effectiveApplication && (
            <div className="rounded-lg bg-app-surface/50 px-3 py-2">
              <p className="text-xs text-app-muted">Application</p>
              <p className="text-sm font-medium text-app-muted">
                {effectiveApplication.company}
                {effectiveApplication.positionTitle && (
                  <span className="text-app-muted font-normal"> · {effectiveApplication.positionTitle}</span>
                )}
              </p>
            </div>
          )}

          {/* Type selector */}
          <div>
            <label className="block text-xs font-medium text-app-muted mb-2">Type</label>
            <div className="flex flex-wrap gap-2">
              {TYPE_OPTIONS.map((option) => {
                const Icon = option.icon
                return (
                  <button
                    key={option.value}
                    onClick={() => setType(option.value)}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      type === option.value
                        ? 'bg-app-highlight text-app-accent'
                        : 'bg-app-surface text-app-muted hover:bg-app-surface'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {option.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-app-muted mb-1.5">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Send follow-up email"
              className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted dark:placeholder:text-app-muted focus:border-app-accent focus:outline-none focus:ring-1 focus:ring-app-accent"
            />
          </div>

          {/* Quick date options */}
          <div>
            <label className="block text-xs font-medium text-app-muted mb-2">When</label>
            <div className="flex flex-wrap gap-2">
              {QUICK_OPTIONS.map((option) => (
                <button
                  key={option.days}
                  onClick={() => handleQuickOption(option.days)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    selectedDate === addDays(option.days)
                      ? 'bg-app-highlight text-app-accent'
                      : 'bg-app-surface text-app-muted hover:bg-app-surface'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date and time picker */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-app-muted mb-1.5">Date</label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-app-muted pointer-events-none" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={formatDateForInput(new Date())}
                  className="w-full rounded-lg border border-app-line bg-app-surface py-2.5 pl-10 pr-3 text-sm text-app-ink focus:border-app-accent focus:outline-none focus:ring-1 focus:ring-app-accent"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-app-muted mb-1.5">Time</label>
              <div className="relative">
                <ClockIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-app-muted pointer-events-none" />
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full rounded-lg border border-app-line bg-app-surface py-2.5 pl-10 pr-3 text-sm text-app-ink focus:border-app-accent focus:outline-none focus:ring-1 focus:ring-app-accent"
                />
              </div>
            </div>
          </div>

          {/* Note (optional) */}
          <div>
            <label className="block text-xs font-medium text-app-muted mb-1.5">Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any additional details..."
              rows={2}
              className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted dark:placeholder:text-app-muted focus:border-app-accent focus:outline-none focus:ring-1 focus:ring-app-accent resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-app-line px-4 py-3">
          <button
            onClick={onCancel}
            className="rounded-lg px-3 py-2 text-sm font-medium text-app-muted hover:text-app-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedDate || !title.trim() || !effectiveApplication}
            className="rounded-lg bg-app-primary px-4 py-2 text-sm font-medium text-white hover:bg-app-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isEditing ? 'Save Changes' : 'Add Reminder'}
          </button>
        </div>
      </div>
    </div>
  )
}
